import { useEffect, useRef, useState } from "react";
import { IOpenOrder } from "src/Interfaces/marketmaking";
import { StepOrderSide } from "src/Interfaces/stepInterfaces";
import {
  FuturesExecutionLiveDetails,
  FuturesExecutionOrderBookResponse,
  FuturesExecutionTaskRow,
} from "src/Requests/futuresMarketMaking";
import "./FuturesSymbolBoard.css";

interface Props {
  task: FuturesExecutionTaskRow | null;
  statistic: FuturesExecutionOrderBookResponse["instrumentStatistic"];
  live: FuturesExecutionLiveDetails | null;
  openOrders: IOpenOrder[];
}

interface Tip {
  x: number;
  top: number;
  value: number;
  title: string;
  volume?: number;
}

const price = (value?: number | null): number | null =>
  value != null && Number.isFinite(value) && value > 0 ? value : null;
const priceRange = (min?: number | null, max?: number | null) => {
  const validMin = price(min);
  const validMax = price(max);
  return validMin != null && validMax != null && validMax > validMin
    ? { min: validMin, max: validMax } : null;
};
const format = (value: number) => value.toLocaleString("en-US", { maximumFractionDigits: 0 });
const position = (value: number, min: number, max: number) =>
  Math.max(0, Math.min(1, (value - min) / (max - min)));
const trackCenter = 161;

const FuturesSymbolBoard = ({ task, statistic, live, openOrders }: Props) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(824);
  const [tip, setTip] = useState<Tip | null>(null);
  const range = priceRange(statistic?.minPricePermit, statistic?.maxPricePermit) ??
    priceRange(task?.minPricePermit, task?.maxPricePermit);
  const min = range?.min ?? null;
  const max = range?.max ?? null;

  useEffect(() => setTip(null), [task?.futureInstrumentId]);

  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    const measure = () => setPlotWidth(plot.getBoundingClientRect().width || 824);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(plot);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [min, max]);

  const previous = range && statistic
    ? price(statistic.priceYesterday) ?? (range.min + range.max) / 2 : null;
  const last = range ? price(live?.lastFuturePrice) ?? price(statistic?.trade?.price) : null;
  const closing = range ? price(statistic?.final?.price) : null;
  const low = range ? price(statistic?.priceMin) : null;
  const high = range ? price(statistic?.priceMax) : null;
  const upward = previous != null && (last ?? closing ?? previous) >= previous;
  const trend = upward ? "#1fa56f" : "#e53b64";
  const pos = (value: number) => range ? position(value, range.min, range.max) : 0;
  const styleAt = (value: number) => ({ left: `${pos(value) * 100}%` });
  const showTip = (value: number, title: string, top: number, volume?: number) => {
    const x = pos(value) * plotWidth;
    setTip({ x: Math.max(Math.min(65, plotWidth / 2),
      Math.min(plotWidth - Math.min(65, plotWidth / 2), x)), top, value, title, volume });
  };
  const hideTip = () => setTip(null);
  const refs = (range ? [
    { key: "VWSP", value: price(live?.vwsp), color: "#94a4b5" },
    { key: "تعادلی", value: price(live?.liveFairPrice), color: "#6687a5" },
    { key: "ارسالی", value: price(live?.sentFairPrice), color: "#456f91" },
    { key: "تئوریک", value: price(live?.fTheor), color: "#2f6fae" },
  ] : []).filter((item): item is { key: string; value: number; color: string } => item.value != null)
    .sort((a, b) => a.value - b.value);
  const refLevels: number[] = [];
  const positionedRefs = refs.map(ref => {
    const x = pos(ref.value) * plotWidth;
    let level = 0;
    while (refLevels[level] != null && x - refLevels[level] < 60) level++;
    refLevels[level] = x;
    return { ...ref, level };
  });
  const offers = (side: StepOrderSide) => !range ? [] : openOrders
    .filter(order => (order.orderSide ?? order.side) === side && price(order.price) != null &&
      order.row != null && order.row > 0)
    .sort((a, b) => a.row! - b.row!)
    .slice(0, 5);

  const renderOffers = (side: StepOrderSide) => offers(side).map((order, index) => {
    const ask = side === StepOrderSide.Sell;
    const code = `${ask ? "A" : "B"}${order.row}`;
    const bubbleTop = 89 - index * 7;
    return <div key={`${side}-${order.row}-${order.price}`} className={`fmb-mm ${ask ? "ask" : "bid"}`}
      style={styleAt(order.price)} tabIndex={0}
      aria-label={`${ask ? "سفارش فروش" : "سفارش خرید"} ${code}: ${format(order.price)}، حجم باز ${format(order.volume)}`}
      onMouseEnter={() => showTip(order.price, `${ask ? "سفارش فروش" : "سفارش خرید"} ${code}`, bubbleTop - 15, order.volume)}
      onFocus={() => showTip(order.price, `${ask ? "سفارش فروش" : "سفارش خرید"} ${code}`, bubbleTop - 15, order.volume)}
      onMouseLeave={hideTip} onBlur={hideTip}>
      <span className="fmb-mm-bubble" style={{ top: bubbleTop }}>{code}</span>
      <span className="fmb-mm-line" style={{ top: bubbleTop + 21,
        height: Math.max(2, trackCenter - bubbleTop - 21) }} />
      <span className="fmb-dot" style={{ top: trackCenter }} />
    </div>;
  });

  const moveOnTrack = (clientX: number) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || !range || !statistic) return;
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    showTip(Math.round(range.min + fraction * (range.max - range.min)), "قیمت روی بازه", 130);
  };
  const change = tip && previous != null ? (tip.value - previous) / previous * 100 : 0;

  return <section className="futures-market-bar" aria-label={task
    ? `تابلوی نماد ${task.futureSymbol || task.futureInstrumentId}` : "تابلوی نماد آتی"} dir="rtl">
    <div className="fmb-chart">
      <div className="fmb-plot" ref={plotRef}>
        <div className="fmb-track" />
        {low != null && high != null && low <= high && <div className="fmb-day-range"
          style={{ left: `${pos(low) * 100}%`, width: `${Math.max(0.2, (pos(high) - pos(low)) * 100)}%`,
            backgroundColor: trend }} />}
        {Array.from({ length: 11 }, (_, index) => <span key={index}
          className={`fmb-tick ${index === 0 || index === 10 ? "edge" : ""}`}
          style={{ left: `${index * 10}%` }} />)}
        {previous != null && <span className="fmb-tick previous" style={styleAt(previous)} />}
        {closing != null && <div className="fmb-marker close" style={{ ...styleAt(closing), color: trend }}
          tabIndex={0} aria-label={`قیمت پایانی ${format(closing)}`}
          onMouseEnter={() => showTip(closing, "قیمت پایانی", 130)}
          onFocus={() => showTip(closing, "قیمت پایانی", 130)}
          onMouseLeave={hideTip} onBlur={hideTip}>
          <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
            <circle cx="9" cy="9" r="6.2" fill="currentColor" stroke="#fff" strokeWidth="1.7" />
          </svg>
        </div>}
        {last != null && <div className="fmb-marker last" style={{ ...styleAt(last), color: trend }}
          tabIndex={0} aria-label={`آخرین معامله ${format(last)}`}
          onMouseEnter={() => showTip(last, "آخرین معامله", 120)}
          onFocus={() => showTip(last, "آخرین معامله", 120)}
          onMouseLeave={hideTip} onBlur={hideTip}>
          <svg width="20" height="18" viewBox="0 0 16 14" aria-hidden="true">
            <polygon points="1,1 15,1 8,13.12" fill="#fff" stroke="currentColor"
              strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        </div>}
        {positionedRefs.map(ref => {
          const badgeTop = ref.level * 20;
          return <div key={ref.key} className="fmb-ref" style={{ ...styleAt(ref.value), color: ref.color }}
            tabIndex={0} aria-label={`${ref.key} ${format(ref.value)}`}
            onMouseEnter={() => showTip(ref.value, ref.key, Math.max(0, badgeTop - 2))}
            onFocus={() => showTip(ref.value, ref.key, Math.max(0, badgeTop - 2))}
            onMouseLeave={hideTip} onBlur={hideTip}>
            <span className="fmb-ref-badge" style={{ top: badgeTop }}>{ref.key}</span>
            <span className="fmb-ref-line" style={{ top: badgeTop + 18,
              height: Math.max(6, trackCenter - badgeTop - 18) }} />
            <span className="fmb-dot" style={{ top: trackCenter }} />
          </div>;
        })}
        {renderOffers(StepOrderSide.Sell)}
        {renderOffers(StepOrderSide.Buy)}
        <div className="fmb-hover-zone" onMouseEnter={event => moveOnTrack(event.clientX)}
          onMouseMove={event => moveOnTrack(event.clientX)} onMouseLeave={hideTip} />
        {range && [
          { value: range.min, label: "حداقل مجاز" },
          ...(previous != null ? [{ value: previous, label: "قیمت دیروز" }] : []),
          { value: range.max, label: "حداکثر مجاز" },
        ].map(item => <div key={item.label}
            className="fmb-axis-label" style={styleAt(item.value)}
            title={item.label}><span>{format(item.value)}</span></div>)}
        {!range && <>
          <div className="fmb-axis-label" style={{ left: "0%" }}>—</div>
          <div className="fmb-axis-label" style={{ left: "100%" }}>—</div>
        </>}
        {tip && <div className="fmb-tooltip" style={{ left: tip.x, top: tip.top }}>
          <div className="fmb-tip-main" dir="ltr"><strong>{format(tip.value)}</strong>
            {previous != null && <span className={change > 0 ? "positive" : change < 0 ? "negative" : ""}>
              ({change >= 0 ? "+" : ""}{change.toFixed(2)}%)
            </span>}
          </div>
          <div className="fmb-tip-title">{tip.title}</div>
          {tip.volume != null && <div className="fmb-tip-extra">حجم باز: {format(tip.volume)}</div>}
        </div>}
      </div>
    </div>
  </section>;
};

export default FuturesSymbolBoard;
